import { CheckmarkFilled } from '@carbon/icons-react';
import {
  Button,
  FormGroup,
  InlineNotification,
  NumberInput,
  PasswordInput,
  Select,
  SelectItem,
  TextInput,
  Toggle,
} from '@carbon/react';
import { asProblem, FormModal, isEmptyValue, problemMessage, requiredMarking, useMarkedLabel } from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  providerApi,
  type DeliveryProvider,
  type LegalEntity,
  type ProviderAccount,
  type ProviderCapability,
  type ProviderSetting,
} from './deliveryApi';

/**
 * Add or change a provider account. The settings come from what the adapter declares, so a new provider needs no new
 * form: plain settings are shown and kept, credentials are write-only (they show only that they are set) and are sent
 * only when something is typed.
 */
export function AccountDialog({
  capability = 'shipping',
  title,
  legalEntities,
  brands,
  providers,
  account,
  onClose,
  onSaved,
}: {
  capability?: ProviderCapability;
  /** The page's title, as the dialog's label. */
  title?: string;
  /** Given for e-invoice accounts, which belong to a legal entity (ADR 0048). */
  legalEntities?: LegalEntity[];
  /** Given for messaging accounts, which belong to a brand or to all of them (ADR 0072). */
  brands?: { id: string; name: string }[];
  providers: DeliveryProvider[];
  account?: ProviderAccount;
  onClose(): void;
  onSaved(): void;
}) {
  const { t } = useTranslation();
  const label = useMarkedLabel();
  const { saveAccount, setCredentials, testAccount } = useMemo(() => providerApi(capability), [capability]);
  const byEntity = legalEntities !== undefined;
  const [legalEntityId, setLegalEntityId] = useState(account?.legalEntityId ?? '');
  const [brandId, setBrandId] = useState(account?.brandId ?? '');
  const [providerId, setProviderId] = useState(account?.providerId ?? providers[0]?.id ?? '');
  const provider = providers.find((p) => p.id === providerId);
  const [name, setName] = useState(account?.name ?? '');
  const [environment, setEnvironment] = useState(account?.environment ?? 'sandbox');
  const [isActive, setIsActive] = useState(account?.isActive ?? true);
  const [settings, setSettings] = useState<Record<string, string>>({ ...(account?.settings ?? {}) });
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [replacing, setReplacing] = useState<Record<string, boolean>>({});
  const [savedId, setSavedId] = useState(account?.id);
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);

  const plain = provider?.settings.filter((s) => !s.isSecret) ?? [];
  const secrets = provider?.settings.filter((s) => s.isSecret) ?? [];
  const isSet = (key: string) => (account?.secretKeys ?? []).includes(key);
  const settingValue = (setting: ProviderSetting) => settings[setting.key] ?? setting.default ?? '';
  // A credential that is already saved (and not being replaced) counts as filled in.
  const secretFilled = (setting: ProviderSetting) =>
    (isSet(setting.key) && !replacing[setting.key]) || !isEmptyValue(typed[setting.key]);

  const marking = requiredMarking([
    { required: true },
    { required: true },
    { required: true },
    ...(byEntity ? [{ required: true }] : []),
    ...plain.map((s) => ({ required: s.required })),
    ...secrets.map((s) => ({ required: s.required })),
  ]);

  // Which required fields are empty, by element id.
  const missing = new Set<string>();
  if (isEmptyValue(name)) missing.add('account-name');
  if (byEntity && isEmptyValue(legalEntityId)) missing.add('account-legal-entity');
  for (const s of plain) {
    if (s.required && s.type !== 'boolean' && isEmptyValue(settingValue(s))) missing.add(`setting-${s.key}`);
  }
  for (const s of secrets) {
    if (s.required && !secretFilled(s)) missing.add(`secret-${s.key}`);
  }
  const touch = (id: string) => setTouched((now) => new Set([...now, id]));
  const invalid = (id: string) => missing.has(id) && (submitted || touched.has(id));
  const requiredText = t('admin.validation.required');

  const save = useMutation({
    mutationFn: async () => {
      const values = Object.fromEntries(
        Object.entries(typed)
          .filter(([, value]) => value.trim() !== '')
          .map(([key, value]) => [key, value.trim()]),
      );
      const saved = await saveAccount(account?.id ?? savedId, {
        providerId,
        name: name.trim(),
        brandId: brands !== undefined && brandId ? brandId : null,
        storeId: null,
        environment,
        settings,
        isActive,
        legalEntityId: byEntity ? legalEntityId : null,
      });
      // Credentials only when something was typed; what is saved stays as it is.
      if (Object.keys(values).length > 0) await setCredentials(saved.id, values);
      return saved;
    },
    onSuccess: (saved) => {
      setSavedId(saved.id);
      setTyped({});
      onSaved();
      onClose();
    },
  });

  const test = useMutation({
    mutationFn: async () => {
      // Test what is saved, so save first when there is something new.
      const saved = account?.id ?? savedId;
      if (!saved) throw { title: t('delivery.providers.dialog.saveFirst') };
      return testAccount(saved);
    },
  });

  const submit = () => {
    setSubmitted(true);
    if (missing.size === 0 && providerId) save.mutate();
  };

  const field = (setting: ProviderSetting) => {
    const id = `setting-${setting.key}`;
    const text = label(t(setting.labelKey as I18nKey, { defaultValue: setting.key }), setting.required, marking);
    const value = settingValue(setting);
    const set = (next: string) => setSettings((s) => ({ ...s, [setting.key]: next }));
    const check = { invalid: invalid(id), invalidText: requiredText, onBlur: () => touch(id) };
    switch (setting.type) {
      case 'choice':
        return (
          <Select
            key={setting.key}
            id={id}
            labelText={text}
            value={value}
            required={setting.required}
            {...check}
            onChange={(e) => set(e.target.value)}
          >
            {(setting.options ?? []).map((o) => (
              <SelectItem key={o} value={o} text={o} />
            ))}
          </Select>
        );
      case 'number':
        return (
          <NumberInput
            key={setting.key}
            id={id}
            label={text}
            value={value === '' ? '' : Number(value)}
            allowEmpty
            required={setting.required}
            {...check}
            onChange={(_, state) => set(state.value === '' ? '' : String(state.value))}
          />
        );
      case 'boolean':
        return (
          <Toggle
            key={setting.key}
            id={id}
            labelText={text}
            labelA={t('delivery.services.assignments.off')}
            labelB={t('delivery.services.assignments.on')}
            toggled={value === 'true'}
            onToggle={(on) => set(String(on))}
          />
        );
      default:
        return (
          <TextInput
            key={setting.key}
            id={id}
            labelText={text}
            value={value}
            required={setting.required}
            {...check}
            onChange={(e) => set(e.target.value)}
          />
        );
    }
  };

  return (
    <FormModal
      title={account ? t('admin.edit') : t('admin.create')}
      label={title ?? t('delivery.providers.title')}
      pending={save.isPending}
      error={save.error ? problemMessage(t, asProblem(save.error)) : null}
      onSubmit={submit}
      onClose={onClose}
    >
      <Select
        id="account-provider"
        labelText={label(t('delivery.providers.dialog.provider'), true, marking)}
        value={providerId}
        required
        disabled={Boolean(account)}
        onChange={(e) => {
          setProviderId(e.target.value);
          setSettings({});
        }}
      >
        {providers.map((p) => (
          <SelectItem key={p.id} value={p.id} text={p.name} />
        ))}
      </Select>
      <TextInput
        id="account-name"
        labelText={label(t('delivery.providers.dialog.name'), true, marking)}
        value={name}
        maxLength={200}
        required
        invalid={invalid('account-name')}
        invalidText={requiredText}
        onBlur={() => touch('account-name')}
        onChange={(e) => setName(e.target.value)}
      />
      {byEntity && (
        <Select
          id="account-legal-entity"
          labelText={label(t('md.fields.legal_entity_id'), true, marking)}
          helperText={t('einvoice.providers.legalEntityHelp')}
          value={legalEntityId}
          required
          invalid={invalid('account-legal-entity')}
          invalidText={requiredText}
          onBlur={() => touch('account-legal-entity')}
          onChange={(e) => setLegalEntityId(e.target.value)}
        >
          <SelectItem value="" text={t('einvoice.providers.chooseLegalEntity')} />
          {legalEntities.map((e) => (
            <SelectItem key={e.id} value={e.id} text={`${e.legalName} (${e.taxCode})`} />
          ))}
        </Select>
      )}
      {brands !== undefined && (
        <Select
          id="account-brand"
          labelText={t('messaging.providers.brand')}
          helperText={t('messaging.providers.brandHelp')}
          value={brandId}
          onChange={(e) => setBrandId(e.target.value)}
        >
          <SelectItem value="" text={t('messaging.providers.allBrands')} />
          {brands.map((b) => (
            <SelectItem key={b.id} value={b.id} text={b.name} />
          ))}
        </Select>
      )}
      <Select
        id="account-environment"
        labelText={label(t('delivery.providers.dialog.environment'), true, marking)}
        value={environment}
        required
        onChange={(e) => setEnvironment(e.target.value)}
      >
        <SelectItem value="sandbox" text={t('delivery.providers.env.sandbox')} />
        <SelectItem value="production" text={t('delivery.providers.env.production')} />
      </Select>
      {plain.map(field)}
      {secrets.length > 0 && (
        <FormGroup legendText={t('delivery.providers.dialog.credentials')}>
          <p className="pos-delivery__hint">{t('delivery.providers.dialog.credentialsHint')}</p>
          {secrets.map((s) => {
            const id = `secret-${s.key}`;
            const text = label(t(s.labelKey as I18nKey, { defaultValue: s.key }), s.required, marking);
            return isSet(s.key) && !replacing[s.key] ? (
              <div key={s.key} className="pos-delivery__secret-set">
                <span>
                  <CheckmarkFilled size={16} aria-hidden="true" /> {text}: {t('delivery.providers.dialog.saved')}
                </span>
                <Button kind="ghost" onClick={() => setReplacing((r) => ({ ...r, [s.key]: true }))}>
                  {t('delivery.providers.dialog.replace')}
                </Button>
              </div>
            ) : (
              <PasswordInput
                key={s.key}
                id={id}
                labelText={text}
                showPasswordLabel={t('carbon.showPassword')}
                hidePasswordLabel={t('carbon.hidePassword')}
                autoComplete="new-password"
                required={s.required}
                invalid={invalid(id)}
                invalidText={requiredText}
                onBlur={() => touch(id)}
                value={typed[s.key] ?? ''}
                onChange={(e) => setTyped((v) => ({ ...v, [s.key]: e.target.value }))}
              />
            );
          })}
        </FormGroup>
      )}
      <Toggle
        id="account-active"
        labelText={t('delivery.providers.dialog.active')}
        labelA={t('delivery.services.assignments.off')}
        labelB={t('delivery.services.assignments.on')}
        toggled={isActive}
        onToggle={setIsActive}
      />
      {(account ?? savedId) && (
        <div className="pos-delivery__test">
          <Button kind="secondary" disabled={test.isPending} onClick={() => test.mutate()}>
            {t('delivery.providers.dialog.test')}
          </Button>
          {test.data && (
            <InlineNotification
              kind={test.data.ok ? 'success' : 'error'}
              role="status"
              lowContrast
              hideCloseButton
              title={
                test.data.ok
                  ? t('delivery.providers.dialog.testOk')
                  : t('delivery.providers.dialog.testFailed', { code: test.data.message ?? '' })
              }
            />
          )}
          {test.error && (
            <InlineNotification
              kind="error"
              role="alert"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(test.error))}
            />
          )}
        </div>
      )}
    </FormModal>
  );
}
